import { useEffect, useState } from "react";
import PageHeader from "../components/layout/PageHeader";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const EMPTY = { code: "", name: "", address: "", phone: "", consortium_id: "", active: true };

export default function Branches() {
  const { user: me } = useAuth();
  const isSuper = me?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [consortiums, setConsortiums] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const [b, c] = await Promise.all([api.get("/branches"), api.get("/consortiums")]);
    setItems(b.data);
    setConsortiums(c.data);
  };
  useEffect(() => { load(); }, []);

  const consortiumName = (id) => consortiums.find((c) => c.id === id)?.name || "—";

  const save = async () => {
    if (isSuper && !editing && !form.consortium_id) {
      toast.error("Debes seleccionar un consorcio");
      return;
    }
    try {
      if (editing) {
        await api.put(`/branches/${editing.id}`, form);
        toast.success("Sucursal actualizada");
      } else {
        await api.post("/branches", form);
        toast.success("Sucursal creada");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Eliminar sucursal ${b.code}?`)) return;
    try {
      await api.delete(`/branches/${b.id}`);
      toast.success("Eliminada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex-1 bg-slate-50 min-h-full">
      <PageHeader
        title="Sucursales"
        subtitle="Bancas y puntos de venta físicos"
        testId="branches-header"
        actions={
          <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700 rounded-sm font-bold" data-testid="branches-create-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva sucursal
          </Button>
        }
      />
      <div className="p-6 sm:p-8">
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <Th>Código</Th><Th>Nombre</Th>{isSuper && <Th>Consorcio</Th>}<Th>Dirección</Th><Th>Teléfono</Th><Th>Estado</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`branch-row-${b.code}`}>
                  <td className="p-3 font-mono font-bold text-slate-950">{b.code}</td>
                  <td className="p-3 text-slate-900">{b.name}</td>
                  {isSuper && <td className="p-3 text-slate-700 text-xs">{consortiumName(b.consortium_id)}</td>}
                  <td className="p-3 text-slate-700">{b.address || "—"}</td>
                  <td className="p-3 font-mono text-slate-700">{b.phone || "—"}</td>
                  <td className="p-3">
                    {b.active ? <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-green-100 text-green-700">Activa</span> :
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-slate-100 text-slate-600">Inactiva</span>}
                  </td>
                  <td className="p-3 text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => { setEditing(b); setForm(b); setOpen(true); }} className="h-8 rounded-sm border-slate-300" data-testid={`branch-edit-${b.code}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => remove(b)} className="h-8 rounded-sm border-red-300 text-red-700" data-testid={`branch-delete-${b.code}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={isSuper ? 7 : 6} className="p-8 text-center text-slate-500 text-sm">Sin sucursales</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader><DialogTitle>{editing ? "Editar sucursal" : "Nueva sucursal"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Código</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="mt-1 rounded-sm font-mono" data-testid="branch-form-code" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="branch-form-name" />
            </div>
            {isSuper && !editing && (
              <div className="col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">Consorcio</Label>
                <Select value={form.consortium_id || undefined} onValueChange={(v) => setForm({ ...form, consortium_id: v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="branch-form-consortium"><SelectValue placeholder="Selecciona un consorcio" /></SelectTrigger>
                  <SelectContent>
                    {consortiums.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} · {c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider">Dirección</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Teléfono</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label className="text-sm">Activa</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700 rounded-sm" data-testid="branch-form-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Th = ({ children }) => <th className="p-3 text-xs uppercase tracking-wider font-bold text-slate-600">{children}</th>;
